import { eventChannel, EventChannel } from 'redux-saga'
import { call, put, take, race, delay, select } from 'redux-saga/effects'
import { Action } from 'infra/types'
import { IAppState } from 'infra/AppState'
import { getEnvConfig } from 'utils/getEnvConfig'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { refreshMerchantTransactions } from 'state/transaction/transaction.actions'
import { fetchCustomerDuesInfo } from 'state/dashboard/dashboard.actions'
import {
  isPushSupported,
  getPushPermission,
  requestPushToken,
  subscribeForegroundMessages,
  toPushSignal,
  isVapidKeyConfigured,
  PUSH_SIGNAL,
  IPushSignal
} from 'services/Tracking/FirebaseMessaging'
import { createOrUpdateDevice, linkDeviceWithBusiness } from 'services/Api/DeviceApi'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { NEWDASHBOARD_EVENTS, LIVE_UPDATES_ACTION } from 'state/new-summary/events/events.types'
import { NavigationActions } from 'state/navigation/navigation.actions'
import { ENTITY_TYPE } from '../../constants'
import {
  RealtimeActions,
  setPushSupported,
  setPushPermission,
  showPushNudge,
  dismissPushNudge,
  setPushRegistered
} from './realtime.actions'

// Re-ask cadence for the 'default' (not-yet-decided) nudge. We re-show it gently
// instead of never-again, but cap it so we don't nag — and Chrome only penalizes
// the NATIVE prompt (fired on Enable), not our in-app nudge.
const NUDGE_LAST_SHOWN_KEY = 'okc_push_nudge_last_shown_at'
const NUDGE_COUNT_KEY = 'okc_push_nudge_count'
const DEFAULT_REASK_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000 // re-show at most weekly
const DEFAULT_MAX_ASKS = 3 // …up to 3 times, then stop
/** Delay before showing the nudge so it eases in after the home page has loaded. */
const NUDGE_SHOW_DELAY_MS = 1800
/** Max time to wait for the user to leave a blocked page before dropping the nudge. */
const NUDGE_ROUTE_WAIT_MAX_MS = 30 * 1000
/** Min gap between visibility-triggered refreshes, so tab-hopping doesn't spam the API. */
const VISIBILITY_REFRESH_THROTTLE_MS = 30 * 1000
/**
 * How often to pull each customer's collection due date while the tab is active.
 * Due dates are a customer-level field (not a transaction), so they don't ride the
 * transaction push signal. A gentle periodic pull — mirroring Android's periodic
 * customer sync — keeps them fresh when changed on another device. ListDuesInfo is a
 * single lightweight call, and we only run it while the tab is visible.
 */
const DUE_DATE_SYNC_INTERVAL_MS = 60 * 1000
/** Coalesce a burst of pushes into a single refresh (Android's WorkManager effect). */
const COALESCE_QUIET_MS = 800
/** Upper bound on how long to keep coalescing under a continuous push stream. */
const COALESCE_MAX_MS = 4000

let lastRefreshAt = 0
let remoteChannelStarted = false
let permissionChannelStarted = false
let vapidWarningShown = false

/**
 * One-time, prominent warning when web push is supported but no VAPID key is
 * configured — the single most common reason realtime sync silently stays off.
 * Fires on any build (push is gated off in `development` anyway), so it surfaces
 * during staging/prod testing where the feature actually runs.
 */
const warnIfVapidKeyMissing = (): void => {
  if (vapidWarningShown || isVapidKeyConfigured()) {
    return
  }
  vapidWarningShown = true
  console.warn(
    '%c[Realtime Push] FIREBASE_VAPID_KEY is not set — web push is disabled.%c\n' +
      'No FCM token can be minted, so transactions from other devices will NOT auto-refresh.\n' +
      'Fix: Firebase Console → Project settings → Cloud Messaging → Web Push certificates → ' +
      'generate a key pair, then set FIREBASE_VAPID_KEY in your env.',
    'font-weight:bold;color:#B45309;background:#FEF3C7;padding:2px 4px;border-radius:3px;',
    'color:#92400E;'
  )
}

const readInt = (key: string): number => {
  try {
    return parseInt(localStorage.getItem(key) || '0', 10) || 0
  } catch {
    return 0
  }
}

/** Whether to (re)show the 'default' nudge: under the ask cap and past the cooldown. */
const shouldReAskDefault = (): boolean => {
  if (readInt(NUDGE_COUNT_KEY) >= DEFAULT_MAX_ASKS) {
    return false
  }
  return Date.now() - readInt(NUDGE_LAST_SHOWN_KEY) >= DEFAULT_REASK_COOLDOWN_MS
}

/** Record that we just showed the nudge (starts the cooldown, counts the ask). */
const recordNudgeShown = (): void => {
  try {
    localStorage.setItem(NUDGE_LAST_SHOWN_KEY, String(Date.now()))
    localStorage.setItem(NUDGE_COUNT_KEY, String(readInt(NUDGE_COUNT_KEY) + 1))
  } catch {
    // ignore storage failures — worst case the cadence resets
  }
}

/** Route path prefixes where the "Get live updates" nudge must never appear. */
export const NUDGE_BLOCKED_ROUTES = ['/subscription-required']

/** True when the current route is one the nudge must not appear on. */
const isNudgeBlockedRoute = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  return NUDGE_BLOCKED_ROUTES.some((path) => window.location.pathname.startsWith(path))
}

/** Reset the nudge counters once push is enabled (fresh start if it ever resets). */
const clearNudgeState = (): void => {
  try {
    localStorage.removeItem(NUDGE_LAST_SHOWN_KEY)
    localStorage.removeItem(NUDGE_COUNT_KEY)
  } catch {
    // ignore
  }
}

/**
 * Get a service worker registration with an ACTIVE worker — getToken() needs one.
 *
 * We REGISTER it here (idempotent) rather than relying on the client bootstrap in
 * client/index.tsx, because that only runs on the initial page load. Relogin is a
 * client-side navigation, so on relogin the bootstrap never re-runs and the saga
 * would find no registration — which is exactly why token/device registration
 * failed after logout+relogin. Registering here makes it self-sufficient.
 *
 * The Firebase config is passed on the query string (the SW can't read
 * window._env_) — same URL the bootstrap uses, so this is a no-op when the SW is
 * already registered. We then wait for an active worker, capped so we never hang.
 */
const getSwRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }
  try {
    const swConfig = new URLSearchParams({
      apiKey: getEnvConfig('FIREBASE_API_KEY') || '',
      projectId: getEnvConfig('FIREBASE_PROJECT_ID') || '',
      messagingSenderId: getEnvConfig('FIREBASE_MESSAGING_SENDER_ID') || '',
      appId: getEnvConfig('FIREBASE_APP_ID') || ''
    }).toString()
    const reg = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${swConfig}`, {
      scope: '/'
    })
    if (reg.active) {
      return reg
    }
    // Freshly registering — wait for it to activate (capped so we never hang).
    const ready = navigator.serviceWorker.ready
    const timeout = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 10000)
    })
    const active = (await Promise.race([ready, timeout])) as ServiceWorkerRegistration | null
    return active || reg
  } catch (e) {
    console.warn('[FCM] getSwRegistration failed', e)
    return null
  }
}

/** Mint an FCM token, register the device, and link it to the business. */
function* registerDeviceForPush(businessId: string | null) {
  // Reflect the (already-decided) permission immediately so the Settings row
  // flips Off→On the moment the user grants — before the slower SW/token work,
  // which may also bail early. Kept in sync again after token minting below.
  yield put(setPushPermission(getPushPermission()))
  const registration: ServiceWorkerRegistration | null = yield call(getSwRegistration)
  if (!registration) {
    return
  }
  const token: string | null = yield call(requestPushToken, registration)
  yield put(setPushPermission(getPushPermission()))
  if (!token) {
    return
  }
  const deviceId: string | null = yield call(createOrUpdateDevice, token)
  if (deviceId && businessId) {
    yield call(linkDeviceWithBusiness, deviceId, businessId)
  }
  yield put(setPushRegistered({ deviceId }))
  yield put(
    pushEvent(NEWDASHBOARD_EVENTS.LIVE_UPDATES, { action: LIVE_UPDATES_ACTION.DEVICE_REGISTERED })
  )
}

/**
 * Block until the current route is one the nudge is allowed on. Resolves the
 * moment a navigation lands on a non-blocked page (e.g. a freshly-subscribed user
 * being redirected off /subscription-required to home). The caller races this
 * against a timeout so we never wait forever if the user stays on a blocked page.
 */
function* waitForAllowedRoute() {
  while (isNudgeBlockedRoute()) {
    yield take(NavigationActions.ROUTE_CHANGE)
  }
  return true
}

/**
 * Entry point (dispatched on business select). Decides, based on notification
 * permission, whether to register immediately or show the contextual nudge.
 */
export function* initRealtimeSyncEffect(action: Action<{ businessId: string }>) {
  if (typeof window === 'undefined') {
    return
  }
  const { businessId } = action.payload

  const supported: boolean = yield call(isPushSupported)
  yield put(setPushSupported(supported))

  if (!supported) {
    yield put(setPushPermission('unsupported'))
    return
  }

  // Push is supported here — surface the most common misconfiguration loudly.
  warnIfVapidKeyMissing()

  const permission = getPushPermission()
  yield put(setPushPermission(permission))

  if (permission === 'granted') {
    // Already granted — (re)register silently so the current business is linked.
    yield call(clearNudgeState)
    yield call(registerDeviceForPush, businessId)
  } else if (permission === 'default' && shouldReAskDefault()) {
    // Contextual nudge: ask in-app first, only hit the browser prompt on opt-in.
    // Re-shown on a gentle cadence (weekly, capped) rather than never again.
    //
    // Hold briefly so the modal eases in AFTER the home ledger has rendered,
    // rather than popping up mid-load. takeLatest cancels this wait if the user
    // switches business before it fires, so a stale nudge never appears.
    yield delay(NUDGE_SHOW_DELAY_MS)
    // The nudge must not appear on blocked/utility pages (e.g. subscription-required).
    // If we're on one after the delay, wait for the user to navigate onto an allowed
    // page before showing — this covers the subscription flow, where a freshly-
    // subscribed user is redirected from /subscription-required to home and should
    // then be asked. Bounded so we never wait forever if they stay on a blocked page.
    if (isNudgeBlockedRoute()) {
      const { unblocked } = yield race({
        unblocked: call(waitForAllowedRoute),
        timeout: delay(NUDGE_ROUTE_WAIT_MAX_MS)
      })
      if (!unblocked) {
        return
      }
    }
    yield call(recordNudgeShown)
    yield put(showPushNudge())
    yield put(
      pushEvent(NEWDASHBOARD_EVENTS.LIVE_UPDATES, { action: LIVE_UPDATES_ACTION.NUDGE_SHOWN })
    )
  }
  // 'denied' → can't re-prompt the browser; the Settings toggle guides the user
  // to enable it manually.
}

/** User opted in (nudge Enable or the Settings toggle) → request permission + register. */
export function* acceptPushNudgeEffect() {
  yield put(dismissPushNudge())
  yield call(clearNudgeState)
  const businessId = AuthStorageInstance.getCurrentBusinessId()
  yield call(registerDeviceForPush, businessId || null)
}

/**
 * One channel merging every "something changed remotely" trigger:
 *  - foreground FCM messages (tab focused)
 *  - service-worker relayed background pushes (tab in background)
 *  - tab regaining visibility (cheap catch-up fallback)
 */
function createRemoteChangeChannel(): EventChannel<IPushSignal | { source: 'visibility' }> {
  return eventChannel((emit) => {
    const unsubForeground = subscribeForegroundMessages((signal) => emit(signal))

    const swHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === 'OKC_PUSH_SIGNAL') {
        emit(toPushSignal(event.data.data) || { name: 'unknown', raw: {} })
      }
    }
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', swHandler)
    }

    const visHandler = () => {
      if (document.visibilityState === 'visible') {
        emit({ source: 'visibility' })
      }
    }
    document.addEventListener('visibilitychange', visHandler)

    return () => {
      unsubForeground()
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', swHandler)
      }
      document.removeEventListener('visibilitychange', visHandler)
    }
  })
}

export function* watchRemoteChangesEffect() {
  if (typeof window === 'undefined' || remoteChannelStarted) {
    return
  }
  remoteChannelStarted = true

  const channel: EventChannel<IPushSignal | { source: 'visibility' }> = yield call(
    createRemoteChangeChannel
  )
  try {
    while (true) {
      const first: IPushSignal | { source: 'visibility' } = yield take(channel)

      // Coalesce a burst of signals into ONE refresh: after the first event keep
      // collecting until a quiet gap (COALESCE_QUIET_MS) or the max window elapses.
      // This mirrors Android's WorkManager unique-work coalescing, and — combined
      // with per-type targeting below — is why one push no longer fans out to four
      // API calls or fires a full refresh per push in a burst.
      let needCustomer = false
      let needSupplier = false
      let needBoth = false
      let sawVisibility = false
      const classify = (evt: IPushSignal | { source: 'visibility' } | null) => {
        if (!evt) {
          return
        }
        if ((evt as { source?: string }).source === 'visibility') {
          sawVisibility = true
          return
        }
        const name = (evt as IPushSignal).name
        if (name === PUSH_SIGNAL.CUSTOMER_TXNS) {
          needCustomer = true
        } else if (name === PUSH_SIGNAL.SUPPLIER_TXNS) {
          needSupplier = true
        } else {
          // subscription_added / unknown → full refresh is the safe default.
          needBoth = true
        }
      }
      classify(first)

      const windowStart = Date.now()
      let collecting = true
      while (collecting) {
        const remaining = COALESCE_MAX_MS - (Date.now() - windowStart)
        if (remaining <= 0) {
          break
        }
        const { evt } = yield race({
          evt: take(channel),
          quiet: delay(Math.min(COALESCE_QUIET_MS, remaining))
        })
        if (evt) {
          classify(evt)
        } else {
          collecting = false
        }
      }

      // Only sync when there's actually a signed-in business to sync.
      if (!AuthStorageInstance.getIsLoggedIn() || !AuthStorageInstance.getCurrentBusinessId()) {
        continue
      }

      const hadPush = needCustomer || needSupplier || needBoth
      const now = Date.now()

      // Pure focus/visibility catch-up (no push in the window) — throttle it so
      // tab-hopping doesn't spam the API. Real push signals always go through.
      if (!hadPush) {
        if (sawVisibility && now - lastRefreshAt >= VISIBILITY_REFRESH_THROTTLE_MS) {
          lastRefreshAt = now
          yield put(refreshMerchantTransactions())
        }
        continue
      }

      lastRefreshAt = now
      // Targeted refresh when the burst named exactly one side (Android parity);
      // otherwise refresh both.
      if (needBoth || (needCustomer && needSupplier)) {
        yield put(refreshMerchantTransactions())
      } else if (needCustomer) {
        yield put(refreshMerchantTransactions({ entityType: ENTITY_TYPE.CUSTOMER }))
      } else {
        yield put(refreshMerchantTransactions({ entityType: ENTITY_TYPE.SUPPLIER }))
      }
    }
  } finally {
    remoteChannelStarted = false
  }
}

/**
 * Keep the app's permission state in sync with the BROWSER's, live.
 *
 * The user can change notification permission OUTSIDE our UI — most importantly
 * un-blocking it from the site-settings panel (🔒 icon), which is the only way
 * back from `denied`. Without this the Settings row would stay "Blocked" until a
 * reload. We subscribe to the Permissions API `change` event and mirror it into
 * redux; if it flips to `granted` and we're not registered yet, we register the
 * device so push actually starts working (not just the label).
 */
export function* watchPermissionChangesEffect() {
  if (
    typeof navigator === 'undefined' ||
    !navigator.permissions ||
    !navigator.permissions.query ||
    permissionChannelStarted
  ) {
    return
  }

  let permissionStatus: PermissionStatus
  try {
    permissionStatus = yield call(
      [navigator.permissions, navigator.permissions.query],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { name: 'notifications' } as any
    )
  } catch {
    // Some browsers reject querying 'notifications' — nothing to watch then.
    return
  }

  permissionChannelStarted = true
  const channel: EventChannel<PermissionState> = eventChannel((emit) => {
    const handler = () => emit(permissionStatus.state)
    permissionStatus.addEventListener('change', handler)
    return () => permissionStatus.removeEventListener('change', handler)
  })

  try {
    while (true) {
      const state: PermissionState = yield take(channel)
      // Permissions API uses 'prompt'; our model calls that 'default'.
      const permission = state === 'prompt' ? 'default' : state
      yield put(setPushPermission(permission))

      if (permission === 'granted') {
        const registered: boolean = yield select((s: IAppState) => s.Realtime.registered)
        if (!registered) {
          const businessId = AuthStorageInstance.getCurrentBusinessId()
          yield call(registerDeviceForPush, businessId || null)
        }
      }
    }
  } finally {
    channel.close()
    permissionChannelStarted = false
  }
}

/**
 * Periodically re-syncs customer collection due dates so a change made on another
 * device (or the Android app) shows up on web without a manual reload. Push/visibility
 * refreshes already pull dues via the customer-list refetch; this covers the remaining
 * case — an idle, focused tab where no push fires. Skips while hidden, signed out, or
 * before the customer list has loaded, to avoid pointless calls.
 */
export function* syncDueDatesEffect() {
  if (typeof window === 'undefined') {
    return
  }
  while (true) {
    yield delay(DUE_DATE_SYNC_INTERVAL_MS)

    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      continue
    }
    if (!AuthStorageInstance.getIsLoggedIn() || !AuthStorageInstance.getCurrentBusinessId()) {
      continue
    }
    const customerCount: number = yield select(
      (s: IAppState) => s.Dashboard.customers.allIds.length
    )
    if (customerCount === 0) {
      continue
    }
    yield put(fetchCustomerDuesInfo())
  }
}
